import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Layout, { PageHeader } from "../components/Layout";
import { api, money, formatApiError, STATUS_LABELS, STATUS_COLORS, TRADE_IN_PAYOUT_LABELS } from "../lib/api";
import { toast } from "sonner";
import { StoreCreditCard } from "../components/StoreCreditCard";
import { ArrowLeft, PencilSimple, User, Wrench, Receipt, Phone, EnvelopeSimple, FloppyDisk, X, IdentificationCard, ShoppingBag } from "@phosphor-icons/react";
import { CustomerAvatar } from "../components/CustomerAvatar";

export default function CustomerDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", notes: "" });
  const [tab, setTab] = useState("all"); // all | tickets | sales | trade_ins

  const load = () => api.get(`/customers/${id}/history`).then((r) => {
    setData(r.data);
    setForm({
      name: r.data.customer.name,
      phone: r.data.customer.phone || "",
      email: r.data.customer.email || "",
      notes: r.data.customer.notes || "",
    });
  });

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/customers/${id}`, form);
      setEditing(false);
      await load();
      toast.success("Customer updated");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (!data) return <Layout><div className="p-8 text-zinc-500">Loading…</div></Layout>;

  const { customer, tickets, sales, trade_ins = [], stats } = data;

  // Merge for "all" timeline — now includes trade-ins as a 3rd event type.
  const timeline = [
    ...tickets.map((t) => ({ kind: "ticket", at: t.created_at, data: t })),
    ...sales.map((s) => ({ kind: "sale", at: s.created_at, data: s })),
    ...trade_ins.map((ti) => ({ kind: "trade_in", at: ti.created_at, data: ti })),
  ].sort((a, b) => new Date(b.at) - new Date(a.at));

  return (
    <Layout>
      <PageHeader
        overline="CUSTOMER"
        title={customer.name}
        actions={
          <>
            <button onClick={() => nav(-1)} className="h-11 px-4 border border-[#E5E7EB] text-[13px] font-semibold flex items-center gap-2 hover:border-[#121216] bg-white">
              <ArrowLeft size={14} weight="bold" /> BACK
            </button>
            {!editing && (
              <button onClick={() => setEditing(true)} data-testid="edit-customer" className="h-11 px-4 bg-[#121216] text-white text-[13px] font-semibold flex items-center gap-2 hover:bg-[#1E5FEB]">
                <PencilSimple size={14} weight="bold" /> EDIT
              </button>
            )}
          </>
        }
      />

      <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: info + stats */}
        <section className="space-y-6">
          <div className="bg-white border border-[#E5E7EB]">
            <div className="p-5 border-b border-[#E5E7EB] flex items-center gap-3">
              <CustomerAvatar seed={customer.id} size={52} testId="customer-detail-avatar" />
              <div className="min-w-0">
                <div className="overline">CUSTOMER</div>
                <div className="font-heading font-bold text-xl tracking-tight truncate">{customer.name}</div>
              </div>
            </div>
            {!editing ? (
              <div className="p-5 space-y-3">
                <Row icon={Phone} label="Phone" value={customer.phone || "—"} mono />
                <Row icon={EnvelopeSimple} label="Email" value={customer.email || "—"} />
                <div>
                  <div className="overline mb-1">Notes</div>
                  <div className="text-sm whitespace-pre-wrap">{customer.notes || "—"}</div>
                </div>
                <div className="pt-3 border-t border-[#E5E7EB] text-[11px] font-mono text-zinc-500">
                  CUSTOMER SINCE · {new Date(customer.created_at).toLocaleDateString()}
                </div>
              </div>
            ) : (
              <form onSubmit={save} className="p-5 space-y-3">
                <Field label="Name"><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={fld} /></Field>
                <Field label="Phone"><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={fld + " font-mono"} /></Field>
                <Field label="Email"><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={fld} /></Field>
                <Field label="Notes"><textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className={`${fld} h-auto py-2`} /></Field>
                <div className="flex gap-2 pt-2">
                  <button type="submit" className="flex-1 h-10 bg-[#121216] hover:bg-[#1E5FEB] text-white text-xs font-semibold flex items-center justify-center gap-2">
                    <FloppyDisk size={14} weight="bold" /> SAVE
                  </button>
                  <button type="button" onClick={() => setEditing(false)} className="h-10 px-4 border border-[#E5E7EB] text-xs flex items-center gap-2">
                    <X size={14} /> CANCEL
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 bg-white border border-[#E5E7EB] divide-x divide-y divide-[#E5E7EB]">
            <Stat label="Total Spent" value={`$${money(stats.total_spent)}`} testid="stat-spent" />
            <Stat label="Visits" value={stats.sales_count} testid="stat-visits" />
            <Stat label="Tickets" value={stats.tickets_count} testid="stat-tickets" />
            <Stat label="Open Tickets" value={stats.open_tickets_count} testid="stat-open" accent={stats.open_tickets_count > 0} />
            <Stat label="Trade-Ins" value={stats.trade_ins_count || 0} testid="stat-tradeins" />
            <Stat label="Traded Value" value={`$${money(stats.total_traded_value || 0)}`} testid="stat-traded-value" />
          </div>

          {/* Store credit balance — only shown when non-zero. Money owed
              TO the customer (they can spend this at POS or in repair). */}
          <StoreCreditCard balance={Number(stats.store_credit_balance || 0)} history={data.credit_history || []} />

          {/* Driver's licence card — only shown when the customer has at
              least a licence number on file. Used by Trade-In compliance
              flow so admins can verify ID at point of sale. */}
          {(customer.licence_number || customer.licence_state || customer.address) && (
            <div className="bg-white border border-[#E5E7EB]" data-testid="customer-licence-card">
              <div className="px-5 py-3 border-b border-[#E5E7EB] flex items-center gap-2">
                <IdentificationCard size={16} weight="bold" className="text-[#1E5FEB]" />
                <div className="overline">ID on file</div>
              </div>
              <div className="p-5 space-y-2.5">
                {customer.licence_number && (
                  <Row icon={IdentificationCard} label="Licence" value={`${customer.licence_number}${customer.licence_state ? ` · ${customer.licence_state}` : ""}`} mono />
                )}
                {customer.address && (
                  <div>
                    <div className="overline mb-1">Address</div>
                    <div className="text-sm whitespace-pre-wrap">{customer.address}</div>
                  </div>
                )}
              </div>
            </div>
          )}
        </section>

        {/* Right: history */}
        <section className="lg:col-span-2">
          <div className="bg-white border border-[#E5E7EB]">
            <div className="flex border-b border-[#E5E7EB]">
              {[
                { id: "all", label: "All activity" },
                { id: "tickets", label: `Repair tickets (${tickets.length})` },
                { id: "sales", label: `Purchases (${sales.length})` },
                { id: "trade_ins", label: `Trade-ins (${trade_ins.length})` },
              ].map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  data-testid={`hist-tab-${t.id}`}
                  className={`px-5 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                    tab === t.id ? "border-[#1E5FEB] text-[#121216]" : "border-transparent text-zinc-500 hover:text-[#121216]"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <div className="p-5">
              {tab === "all" && (
                <Timeline items={timeline} />
              )}
              {tab === "tickets" && (
                tickets.length === 0
                  ? <Empty icon={Wrench} label="No repair history yet." />
                  : <TicketsList items={tickets} />
              )}
              {tab === "sales" && (
                sales.length === 0
                  ? <Empty icon={Receipt} label="No purchases yet." />
                  : <SalesList items={sales} />
              )}
              {tab === "trade_ins" && (
                trade_ins.length === 0
                  ? <Empty icon={ShoppingBag} label="No trade-ins yet." />
                  : <TradeInsList items={trade_ins} />
              )}
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}

function Timeline({ items }) {
  if (items.length === 0) return <Empty icon={User} label="No activity yet." />;
  // Pick the icon + accent colour per event kind so the timeline reads at
  // a glance. Trade-ins use amber to differentiate from the blue
  // (ticket) and black (sale) markers.
  const KIND_STYLES = {
    ticket:   { bg: "bg-[#1E5FEB]", Icon: Wrench },
    sale:     { bg: "bg-[#121216]", Icon: Receipt },
    trade_in: { bg: "bg-[#92400E]", Icon: ShoppingBag },
  };
  return (
    <div className="relative">
      <div className="absolute left-[11px] top-0 bottom-0 w-px bg-[#E5E7EB]" />
      <div className="space-y-5">
        {items.map((it, idx) => {
          const s = KIND_STYLES[it.kind] || KIND_STYLES.sale;
          return (
            <div key={idx} className="relative pl-8" data-testid={`timeline-${it.kind}-${idx}`}>
              <div className={`absolute left-0 top-1 w-[23px] h-[23px] flex items-center justify-center ${s.bg} text-white`}>
                <s.Icon size={12} weight="bold" />
              </div>
              {it.kind === "ticket"   && <TicketCard t={it.data} />}
              {it.kind === "sale"     && <SaleCard s={it.data} />}
              {it.kind === "trade_in" && <TradeInCard ti={it.data} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TicketsList({ items }) {
  return (
    <div className="space-y-3">
      {items.map((t) => <TicketCard key={t.id} t={t} />)}
    </div>
  );
}

function SalesList({ items }) {
  return (
    <div className="space-y-3">
      {items.map((s) => <SaleCard key={s.id} s={s} />)}
    </div>
  );
}

function TradeInsList({ items }) {
  return (
    <div className="space-y-3">
      {items.map((ti) => <TradeInCard key={ti.id} ti={ti} />)}
    </div>
  );
}

function TradeInCard({ ti }) {
  // Status badge palette — mirrors backend statuses (held / listed /
  // sold / void).
  const status = (ti.status || "held").toLowerCase();
  const statusColor = ({
    held:   "#92400E",
    listed: "#1E5FEB",
    sold:   "#16A34A",
    void:   "#9CA3AF",
  }[status]) || "#92400E";
  return (
    <Link
      to={`/trade-ins?ti=${ti.id}`}
      className="block border border-[#E5E7EB] hover:border-[#92400E] p-3 transition-colors"
      data-testid={`tradein-${ti.trade_in_number}`}
    >
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <span className="font-mono text-[11px] text-zinc-500">{ti.trade_in_number}</span>
          <span
            className="text-[10px] uppercase tracking-wider px-2 py-0.5 text-white font-semibold"
            style={{ background: statusColor }}
          >
            {status}
          </span>
        </div>
        <div className="font-mono font-bold text-sm">
          ${money(ti.buy_price || 0)} <span className="text-[10px] text-zinc-400">→ ${money(ti.sell_price || 0)}</span>
        </div>
      </div>
      <div className="text-sm font-medium">{ti.device_make} {ti.device_model}{ti.storage ? ` ${ti.storage}` : ""}</div>
      <div className="text-xs text-zinc-500 truncate">
        {ti.imei ? `IMEI ${ti.imei}` : "No IMEI"} · {ti.condition || "—"} · Paid by {(TRADE_IN_PAYOUT_LABELS[ti.payout_method] || "Cash").toLowerCase()}
      </div>
      <div className="mt-2 text-[10px] font-mono text-zinc-400 tracking-wider">
        {new Date(ti.created_at).toLocaleString()} · {ti.intake_store_name || ti.store_name || "—"}
      </div>
    </Link>
  );
}

function TicketCard({ t }) {
  return (
    <Link to={`/repairs/${t.id}`} className="block border border-[#E5E7EB] hover:border-[#121216] p-3 transition-colors" data-testid={`ticket-${t.ticket_number}`}>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <span className="font-mono text-[11px] text-zinc-500">{t.ticket_number}</span>
          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 text-white font-semibold" style={{ background: STATUS_COLORS[t.status] }}>
            {STATUS_LABELS[t.status]}
          </span>
        </div>
        <div className="font-mono font-bold text-sm">${money(t.final_cost || t.estimated_cost)}</div>
      </div>
      <div className="text-sm font-medium">{t.device_make} {t.device_model}</div>
      <div className="text-xs text-zinc-500 truncate">{t.issue}</div>
      <div className="mt-2 text-[10px] font-mono text-zinc-400 tracking-wider">
        {new Date(t.created_at).toLocaleString()} · {t.store_name}
      </div>
    </Link>
  );
}

function SaleCard({ s }) {
  return (
    <Link to={`/sales/${s.id}`} className="block border border-[#E5E7EB] hover:border-[#121216] p-3 transition-colors" data-testid={`sale-${s.sale_number}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="font-mono text-[11px] text-zinc-500">{s.sale_number}</span>
        <div className="font-mono font-bold text-sm">${money(s.total)}</div>
      </div>
      <div className="text-sm">
        {s.items.length} item{s.items.length === 1 ? "" : "s"} · {s.payment_method.replace("_", " ")}
      </div>
      <div className="text-xs text-zinc-500 truncate">
        {s.items.map((i) => `${i.quantity}× ${i.name}`).join(" · ")}
      </div>
      <div className="mt-2 text-[10px] font-mono text-zinc-400 tracking-wider">
        {new Date(s.created_at).toLocaleString()} · {s.store_name}
      </div>
    </Link>
  );
}

function Row({ icon: Icon, label, value, mono }) {
  return (
    <div className="flex items-center gap-3">
      <Icon size={16} className="text-zinc-400" />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">{label}</div>
        <div className={`text-sm ${mono ? "font-mono" : ""} truncate`}>{value}</div>
      </div>
    </div>
  );
}

function Stat({ label, value, testid, accent }) {
  return (
    <div className="p-4" data-testid={testid}>
      <div className="overline mb-1">{label}</div>
      <div className={`font-mono font-bold text-2xl leading-none ${accent ? "text-[#DC2626]" : ""}`}>{value}</div>
    </div>
  );
}

function Empty({ icon: Icon, label }) {
  return (
    <div className="text-center py-10 text-zinc-400">
      <Icon size={32} weight="duotone" className="mx-auto mb-2" />
      <div className="text-sm">{label}</div>
    </div>
  );
}

const fld = "w-full h-10 px-3 border border-[#E5E7EB] focus:border-[#1E5FEB] focus:outline-none text-sm rounded-none bg-white";
function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-[0.15em] text-zinc-500 font-semibold mb-1">{label}</div>
      {children}
    </label>
  );
}
